import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  blockUserApi,
  bookingTransition,
  confirmBookingApi,
  createReport,
  createShareLink,
  getBooking,
  markNoShowApi,
  rateBookingApi,
} from '../lib/api';
import { inConfirmWindow } from '../lib/datetime';
import { BackButton } from '../lib/BackButton';
import { MainButton } from '../lib/MainButton';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { jsLocale, translateError, useT } from '../lib/i18n';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { ContactRow } from '../components/ContactRow';
import { SafetySheet } from '../components/SafetySheet';
import { BottomSheet } from '../components/BottomSheet';
import { TripStatusPanel } from '../components/TripStatusPanel';
import { TextArea } from '../components/TextInput';
import { Select } from '../components/Select';
import { hapticError, hapticSoft, hapticSuccess, hapticTap, hapticWarning, getWebApp, showConfirm } from '../lib/telegram';

export function BookingDetailPage({ bookingId }: { bookingId: string }): JSX.Element {
  const { navigate } = useRouter();
  const { me } = useSession();
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('other');
  const [reportNote, setReportNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [safetyOpen, setSafetyOpen] = useState(false);

  const q = useQuery({ queryKey: ['booking', bookingId], queryFn: () => getBooking(bookingId) });

  const transition = useMutation({
    mutationFn: (action: 'approve' | 'decline' | 'cancel' | 'complete') =>
      bookingTransition(bookingId, action),
    onMutate: async (action) => {
      // Optimistic update: flip booking.status immediately so the driver doesn't
      // re-tap the approve button while waiting for the server.
      await qc.cancelQueries({ queryKey: ['booking', bookingId] });
      const prev = qc.getQueryData<typeof q.data>(['booking', bookingId]);
      if (prev) {
        const nextStatus =
          action === 'approve'
            ? 'approved'
            : action === 'decline'
              ? 'declined'
              : action === 'complete'
                ? 'completed'
                : prev.status.startsWith('cancelled')
                  ? prev.status
                  : 'cancelled_by_driver';
        qc.setQueryData(['booking', bookingId], { ...prev, status: nextStatus });
      }
      return { prev };
    },
    onError: (e: Error, _action, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['booking', bookingId], ctx.prev);
      setActionError(translateError(t, e));
    },
    onSuccess: () => {
      hapticSuccess();
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['booking', bookingId] }),
        qc.invalidateQueries({ queryKey: ['my-bookings'] }),
        qc.invalidateQueries({ queryKey: ['incoming-bookings'] }),
      ]);
    },
  });

  const confirm = useMutation({
    mutationFn: () => confirmBookingApi(bookingId),
    onSuccess: async () => {
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['booking', bookingId] });
    },
    onError: (e: Error) => {
      hapticError();
      setActionError(translateError(t, e));
    },
  });

  const noShow = useMutation({
    mutationFn: () => markNoShowApi(bookingId),
    onMutate: async () => {
      // Optimistic: flip booking -> 'no_show' so the driver's action resolves
      // instantly and the no-show button can't be double-tapped.
      await qc.cancelQueries({ queryKey: ['booking', bookingId] });
      const prev = qc.getQueryData<typeof q.data>(['booking', bookingId]);
      if (prev) qc.setQueryData(['booking', bookingId], { ...prev, status: 'no_show' });
      return { prev };
    },
    onSuccess: () => {
      hapticSuccess();
    },
    onError: (e: Error, _vars, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['booking', bookingId], ctx.prev);
      setActionError(translateError(t, e));
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['booking', bookingId] });
    },
  });

  const share = useMutation({
    mutationFn: () => createShareLink(bookingId),
    onSuccess: (res) => {
      hapticSuccess();
      const shareUrl =
        `https://t.me/share/url?url=${encodeURIComponent(res.url)}` +
        `&text=${encodeURIComponent(t('share.text'))}`;
      const tg = getWebApp();
      if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
      else if (navigator.share) void navigator.share({ url: res.url, text: t('share.text') });
      else window.open(shareUrl, '_blank');
    },
    onError: (e: Error) => {
      hapticError();
      setActionError(translateError(t, e));
    },
  });

  const rate = useMutation({
    mutationFn: () => rateBookingApi(bookingId, { stars, comment: comment || undefined }),
    onMutate: async () => {
      // Optimistic: flip the viewer's rated flag so the rating card disappears
      // immediately instead of waiting for the round-trip.
      await qc.cancelQueries({ queryKey: ['booking', bookingId] });
      const prev = qc.getQueryData<typeof q.data>(['booking', bookingId]);
      if (prev) {
        const viewerIsPassenger = me?.id === prev.passenger.id;
        qc.setQueryData(['booking', bookingId], {
          ...prev,
          ratedByPassenger: viewerIsPassenger ? true : prev.ratedByPassenger,
          ratedByDriver: viewerIsPassenger ? prev.ratedByDriver : true,
        });
      }
      return { prev };
    },
    onSuccess: () => {
      hapticSuccess();
      setComment('');
    },
    onError: (e: Error, _vars, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['booking', bookingId], ctx.prev);
      setActionError(translateError(t, e));
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['booking', bookingId] });
    },
  });

  const blockMut = useMutation({
    mutationFn: () => blockUserApi(b!.passenger.id),
    onSuccess: () => {
      hapticSuccess();
      navigate('/my/bookings');
    },
    onError: (e: Error) => {
      hapticError();
      setActionError(translateError(t, e));
    },
  });

  const submitReport = useMutation({
    mutationFn: () =>
      createReport({
        bookingId,
        reason: reportReason,
        description: reportNote || undefined,
      }),
    onSuccess: () => {
      hapticSoft();
      setReportOpen(false);
      setReportNote('');
    },
  });

  if (q.isLoading) {
    return (
      <div className="mx-auto max-w-md p-6">
        <div className="h-48 animate-pulse rounded-card bg-tg-bg-secondary" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <p className="text-destructive">{t('booking.cantLoad')}</p>
      </div>
    );
  }

  const b = q.data;
  const isPassenger = me?.id === b.passenger.id;
  const isDriver = !isPassenger;

  const canDecide = isDriver && b.status === 'pending';
  const canCancel =
    b.status === 'pending' || (b.status === 'approved' && (isPassenger || isDriver));
  // A passenger marks they've hopped off ("I've arrived") — but ONLY once the
  // driver has actually started the trip (ride === 'departed'). The driver
  // finishes the whole ride from the ride screen, which completes remaining bookings.
  const rideStarted = b.ride?.status === 'departed';
  const canComplete = b.status === 'approved' && isPassenger && rideStarted;
  // Driver can mark a no-show once the trip has started and the passenger
  // never appeared. Penalises the passenger's reliability (autonomous).
  const canMarkNoShow = b.status === 'approved' && isDriver && rideStarted;
  const canRate =
    b.status === 'completed' && !(isPassenger ? b.ratedByPassenger : b.ratedByDriver);

  const openTgChat = (): void => {
    const username = b.passenger.telegramUsername;
    if (!username) return;
    const url = `https://t.me/${username}`;
    const tg = getWebApp();
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
  };

  const reasonOptions = [
    { value: 'no_show', label: t('booking.report.reason.no_show') },
    { value: 'dangerous_driving', label: t('booking.report.reason.dangerous_driving') },
    { value: 'overcharging', label: t('booking.report.reason.overcharging') },
    { value: 'commercial_activity', label: t('booking.report.reason.commercial_activity') },
    { value: 'harassment', label: t('booking.report.reason.harassment') },
    { value: 'spam', label: t('booking.report.reason.spam') },
    { value: 'other', label: t('booking.report.reason.other') },
  ];

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-tg-hint">{t('booking.title')}</p>
        {b.ride ? (
          <>
            <h1 className="text-2xl font-bold text-tg-text">
              {b.ride.originName} → {b.ride.destinationName}
            </h1>
            <p className="text-sm text-tg-hint">
              {new Date(b.ride.departureAt).toLocaleDateString(jsLocale(locale), {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
              {' · '}
              {new Date(b.ride.departureAt).toLocaleTimeString(jsLocale(locale), {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </p>
            {b.fromCity && b.toCity && (
              <p className="text-sm font-medium text-brand-green">
                {locale === 'en' ? b.fromCity.nameEn : b.fromCity.nameSr} →{' '}
                {locale === 'en' ? b.toCity.nameEn : b.toCity.nameSr}
                {' · '}
                {Math.round(b.segmentPrice).toLocaleString()} {b.ride?.currency ?? ''}
              </p>
            )}
          </>
        ) : (
          <h1 className="text-2xl font-bold text-tg-text">#{b.id.slice(-6)}</h1>
        )}
        <div className="pt-1">
          <Badge
            tone={
              b.status === 'pending' ? 'warning' : b.status === 'approved' ? 'success' : 'neutral'
            }
          >
            {t(`bs.${b.status}` as 'bs.pending')}
          </Badge>
        </div>
      </header>

      <Card className="mt-4 space-y-2">
        <CardTitle>{isDriver ? t('booking.passenger') : t('booking.details')}</CardTitle>
        {isDriver && (
          <button
            type="button"
            onClick={() => navigate(`/profile/${b.passenger.id}`)}
            className="flex items-center gap-3"
          >
            {b.passenger.avatarUrl ? (
              <img src={b.passenger.avatarUrl} alt="" className="h-10 w-10 rounded-full" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy text-brand-cream">
                {b.passenger.displayName.slice(0, 1)}
              </div>
            )}
            <div className="text-left">
              <p className="font-semibold text-tg-text">{b.passenger.displayName}</p>
              <p className="text-xs text-tg-hint">
                {t('profile.ridesCompleted', { n: b.passenger.ridesCompleted })}
                {b.passenger.ratingAvg !== null && ` · ★ ${b.passenger.ratingAvg.toFixed(1)}`}
              </p>
            </div>
          </button>
        )}
        <CardHint>
          {b.seats} {b.seats === 1 ? t('common.seat_one') : t('common.seat_other')} ·{' '}
          {new Date(b.createdAt).toLocaleDateString()}
          {b.luggage && ` · ${t('booking.luggage')}: ${t(`luggage.${b.luggage}` as 'luggage.medium')}`}
        </CardHint>
        {b.companions && b.companions.length > 0 && (
          <CardHint>
            {t('booking.companions')}: {b.companions.join(', ')}
          </CardHint>
        )}
        {b.messageToDriver && (
          <p className="rounded-input bg-tg-bg p-2 text-sm text-tg-text">"{b.messageToDriver}"</p>
        )}
      </Card>

      <button
        type="button"
        onClick={() => navigate(`/ride/${b.rideId}`)}
        className="mt-3 text-left text-sm font-medium text-tg-link"
      >
        {t('booking.openRide')} →
      </button>

      {/* Trip confirmation — only in the 2h window before departure. */}
      {isPassenger &&
        b.status === 'approved' &&
        b.ride &&
        inConfirmWindow(b.ride.departureAt) && (
          <Card className="mt-3 space-y-2">
            <CardTitle>{t('confirm.title')}</CardTitle>
            {b.confirmedAt ? (
              <p className="text-sm font-medium text-brand-green">✓ {t('confirm.done')}</p>
            ) : (
              <>
                <CardHint>{t('confirm.pending')}</CardHint>
                <button
                  type="button"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                  className="w-full rounded-input bg-brand-green py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {t('confirm.cta')}
                </button>
              </>
            )}
          </Card>
        )}

      {/* Trip-status timeline — only meaningful once approved (driver may have started posting updates). */}
      {['approved', 'completed', 'rated', 'auto_closed'].includes(b.status) && (
        <TripStatusPanel rideId={b.rideId} asDriver={false} canRead />
      )}

      <Card className="mt-3 space-y-2">
        <CardTitle>{t('contact.title')}</CardTitle>
        {!['approved', 'completed', 'rated', 'auto_closed'].includes(b.status) ? (
          <CardHint>{t('contact.beforeApproval')}</CardHint>
        ) : (
          <div className="space-y-2">
            <ContactRow
              label={t('contact.tgUsername')}
              value={b.passenger.telegramUsername ? `@${b.passenger.telegramUsername}` : t('contact.notShared')}
              actionLabel={b.passenger.telegramUsername ? t('contact.tgButton') : undefined}
              onAction={b.passenger.telegramUsername ? openTgChat : undefined}
            />
            <ContactRow
              label={t('contact.phone')}
              value={b.passenger.phone ?? t('contact.notShared')}
              actionLabel={b.passenger.phone ? t('contact.callButton') : undefined}
              onAction={
                b.passenger.phone
                  ? () => {
                      window.location.href = `tel:${b.passenger.phone}`;
                    }
                  : undefined
              }
            />
          </div>
        )}
      </Card>

      {canRate && (
        <Card className="mt-3 space-y-2">
          <CardTitle>{t('booking.rate.title')}</CardTitle>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  if (n !== stars) hapticTap();
                  setStars(n);
                }}
                className={
                  'min-h-[44px] min-w-[44px] rounded-input border px-3 py-2 text-lg ' +
                  (stars >= n
                    ? 'border-brand-green bg-brand-green/10 text-brand-green'
                    : 'border-[var(--border-light)] text-tg-hint')
                }
                aria-label={`${n}`}
              >
                ★
              </button>
            ))}
          </div>
          <TextArea
            id="rate-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={280}
            placeholder={t('booking.rate.commentPh')}
          />
          <button
            type="button"
            onClick={() => rate.mutate()}
            disabled={rate.isPending}
            className="rounded-input bg-brand-green px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {rate.isPending ? t('booking.rate.sending') : t('booking.rate.submit')}
          </button>
        </Card>
      )}

      {actionError && (
        <p className="mt-3 rounded-card bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </p>
      )}

      {/* Safety card — shown only during the active window (booking approved/completed). */}
      {['approved', 'completed'].includes(b.status) && (
        <Card className="mt-3 space-y-2 border-2 border-destructive/30 bg-destructive/5">
          <CardTitle>{t('safety.cardTitle')}</CardTitle>
          <CardHint>{t('safety.cardBody')}</CardHint>
          <button
            type="button"
            onClick={() => setSafetyOpen(true)}
            className="w-full rounded-input bg-destructive py-3 text-base font-semibold text-white shadow-sm"
          >
            {t('safety.openSheet')}
          </button>
          {isPassenger && (
            <button
              type="button"
              onClick={() => share.mutate()}
              disabled={share.isPending}
              className="w-full rounded-input bg-brand-navy py-2.5 text-sm font-semibold text-brand-cream disabled:opacity-50"
            >
              {t('share.cta')}
            </button>
          )}
        </Card>
      )}

      {safetyOpen && <SafetySheet bookingId={b.id} onClose={() => setSafetyOpen(false)} />}

      {canMarkNoShow && (
        <div className="mt-4">
          <button
            type="button"
            onClick={async () => {
              const ok = await showConfirm(
                t('booking.noShowConfirm', { name: b.passenger.displayName }),
              );
              if (ok) noShow.mutate();
            }}
            disabled={noShow.isPending}
            className="w-full rounded-input bg-tg-bg-secondary py-3 text-sm font-semibold text-[var(--warning)] disabled:opacity-50"
          >
            {t('booking.noShow')}
          </button>
        </div>
      )}
      {canDecide ? (
        <>
          {/* Decline is the secondary action, rendered inline above MainButton
              so it lives in the scrolling content and never gets hidden by the
              virtual keyboard. Approve is the primary action via MainButton —
              Telegram always renders that above the keyboard. */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => transition.mutate('decline')}
              disabled={transition.isPending}
              className="w-full rounded-input bg-tg-bg-secondary py-3 text-sm font-semibold text-destructive disabled:opacity-50"
            >
              {t('booking.decision.decline')}
            </button>
          </div>
          <MainButton
            text={t('booking.decision.approve')}
            loading={transition.isPending}
            onClick={() => transition.mutate('approve')}
          />
        </>
      ) : canComplete ? (
        <MainButton
          text={t('booking.arrived')}
          loading={transition.isPending}
          onClick={() => transition.mutate('complete')}
        />
      ) : canCancel ? (
        <MainButton
          text={t('booking.cancel')}
          variant="destructive"
          loading={transition.isPending}
          onClick={() => transition.mutate('cancel')}
        />
      ) : null}

      <div className="mt-4 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => setReportOpen((v) => !v)}
          className="text-center text-xs font-medium text-tg-hint underline"
        >
          {t('booking.reportLink')}
        </button>
        <button
          type="button"
          onClick={async () => {
            hapticWarning();
            const ok = await showConfirm(t('block.confirm', { name: b.passenger.displayName }));
            if (ok) blockMut.mutate();
          }}
          disabled={blockMut.isPending}
          className="text-center text-xs font-medium text-destructive underline"
        >
          {t('block.button')}
        </button>
      </div>

      {reportOpen && (
        <BottomSheet onClose={() => setReportOpen(false)} title={t('booking.reportLink')}>
          <div className="space-y-2">
            <Select
              id="report-reason"
              label={t('booking.report.reason')}
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              options={reasonOptions}
            />
            <TextArea
              id="report-note"
              label={t('booking.report.note')}
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              maxLength={1000}
            />
            <button
              type="button"
              onClick={() => submitReport.mutate()}
              disabled={submitReport.isPending}
              className="w-full rounded-input bg-destructive px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t('booking.report.submit')}
            </button>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
